import { useState, type SubmitEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Archive,
  Ban,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  ChevronLeft,
  Flag,
  GitPullRequest,
  Layers,
  MessageSquare,
  Pen,
  Pencil,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
  Ticket,
  X,
} from 'lucide-react';
import {
  type IncidentAffectedDevice,
  type IncidentRemediationOption,
  type IncidentSeverity,
  type IncidentStatus,
  type IncidentSuppression,
  useAcknowledgeIncident,
  useAddComment,
  useArchiveIncident,
  useIncident,
  useIncidentEvents,
  useIncidentSuppressions,
  useRelatedIncidents,
  useResolveIncident,
  useSuppressIncident,
  useSuppressionImpactFetcher,
  useUnsuppressIncident,
  useUpdateIncident,
} from '@/hooks/useIncidents';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  ExecuteCooldownError,
  useAlertDefinition,
  useExecuteAlertDefinition,
} from '@/hooks/useAlertDefinitions';
import { useCreateCMFromIncident } from '@/features/changeManagement';
import { useDivisions } from '@/hooks/useDivisions';
import { useFeature } from '@/hooks/useCapabilities.ts';
import { useConfirm } from '@/hooks/useConfirm';
import { preferredContext, useAvailableContextsInOrg } from '@/hooks/useContexts';
import { useActiveContext } from '@/hooks/useActiveContext';
import {
  MUTE_SOURCE_INCIDENT,
  useMuteSource,
  useNotificationMutes,
  useUnmuteSource,
} from '@/hooks/useNotifications';
import { IncidentNotifications } from '@/components/notifications/IncidentNotifications.tsx';
import { usePermission } from '@/hooks/usePermission';
import { Markdown } from '@/components/Markdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { IncidentMetricChart } from '@/components/incidents/IncidentMetricChart';
import { IncidentRollupPanel } from '@/components/incidents/IncidentRollupPanel';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AppSheet } from '@/components/ui/app-sheet';
import { PageContainer } from '@/components/ui/page-container';
import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';
import { QuietBadge } from '@/components/incidents/QuietBadge.tsx';
import { IncidentTickets } from '@/components/psa/IncidentTickets.tsx';
import { integrationActorLabel, psaVisibilityLabel } from '@/components/psa/timeline.ts';
import { useRollout, ROLLOUTS } from '@/hooks/useRollout.ts';

/**
 * IncidentDetails — single-incident page. Spec §4 MF (oncall-critical).
 *
 * Layout:
 *   lg+: 2/3 main column + 1/3 sidecar (details table + alert info)
 *   sm/md: stacked, sidecar above the comment form for ack/resolve
 *           context to be visible before scrolling
 *
 * Action surfaces:
 *   • PageHeader primary: Acknowledge / Resolve / Archive (state-driven)
 *   • PageHeader secondary: Edit, Suppress for everyone (when
 *     alert-driven and permitted)
 *   • Add-comment textarea + button in the main column
 *   • Re-run alert button in the sidecar (when this incident was fired
 *     by an alert) — cooldown error surfaces as inline notice
 *   • Mute for me in the sidecar's Notifications card — deliberately
 *     not in the header group with Suppress (see SUPPRESS_HELPER below)
 *
 * Edit and Suppress are AppSheet overlays — preserved from Bulma but
 * on the responsive sheet/dialog primitive.
 *
 * Timeline is rendered inline (not yet a shared <TimelineEvent>
 * primitive — addendum §9.9 lands when CMDetails also needs it).
 */

type RerunNotice = { kind: 'success' | 'warning' | 'error'; text: string };

/**
 * Suppress and Mute are one careless click apart and do categorically different
 * things: **suppress** stops the alert firing for the whole organization; **mute**
 * stops Witina telling *you*. Getting it wrong means either silencing a real alert
 * fleet-wide or failing to quiet your own inbox — so the labels say who is affected
 * rather than leaving it to help text, and the two controls deliberately do not sit
 * in the same button group (Suppress with the operational actions in the header,
 * Mute in the Notifications card).
 */
const SUPPRESS_HELPER =
  'Stops this alert firing for your whole organization until the override is removed. Other people will not be notified either.';
const MUTE_HELPER =
  'You stop getting notified about this incident. It stays open and everyone else is still notified.';
const MUTED_HELPER =
  'You stopped getting notified about this incident. It stays open and everyone else is still notified.';

/**
 * What an override covers, in words, from the shape of the row.
 *
 * The wording carries the decision the user is about to make: removing a
 * region-scoped bulk silence un-silences a whole region, and "Remove suppression"
 * looks identical either way, so the scope has to be on screen next to the button.
 */
function suppressionScopeLabel(s: IncidentSuppression): string {
  // `scopedToThisIncident` first, and not re-derived here: the server decides it
  // from the dedupe pattern, which is the only signal for the cross-device audits
  // that raise incidents with no device at all. Deriving it from `deviceId` locally
  // reported those as covering the whole alert.
  if (s.scopedToThisIncident) return 'only this finding';
  if (s.deviceId) return 'every finding on this device';
  if (s.regionId) return 'the whole region';
  if (s.divisionId) return 'the whole division';
  return 'every device this alert covers';
}

/** "indefinitely" or "until <date>" — an expired silence never reaches the UI. */
function suppressionWindowLabel(s: IncidentSuppression): string {
  if (!s.silenceUntil) return 'indefinitely';
  const until = new Date(s.silenceUntil);
  return Number.isNaN(until.getTime()) ? 'indefinitely' : `until ${format(until, 'PPp')}`;
}

export default function IncidentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Beta: the incident's PSA tickets (docs/PsaIntegration.md).
  const psaTicketing = useRollout(ROLLOUTS.psaTicketing);

  // Edit-modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSeverity, setEditSeverity] = useState<IncidentSeverity>('medium');

  // Suppress-modal state
  const [isSuppressOpen, setIsSuppressOpen] = useState(false);
  const [suppressHours, setSuppressHours] = useState<number>(24);
  const [suppressReason, setSuppressReason] = useState('');
  // Defaults to this partition alone. Widening to the whole finding is a deliberate
  // second choice, not the path of least resistance — it silences audiences whose
  // devices the caller may never have seen.
  const [suppressScope, setSuppressScope] = useState<'incident' | 'finding'>('incident');

  // Remediate-modal state
  const [isRemediateOpen, setIsRemediateOpen] = useState(false);
  const [remediateContextId, setRemediateContextId] = useState('');
  const [selectedRemediationId, setSelectedRemediationId] = useState('');
  const [remediateWholeFinding, setRemediateWholeFinding] = useState(false);

  // Comments + notices
  const [commentText, setCommentText] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rerunNotice, setRerunNotice] = useState<RerunNotice | null>(null);

  const { data: incident, isLoading, isError, error } = useIncident(id);
  const { data: events, isLoading: eventsLoading } = useIncidentEvents(id);
  const { data: firingTask } = useAlertDefinition(incident?.taskId ?? undefined);

  // The incident's organization, not the viewer's: that is what the API checks
  // the chosen context against, so anything else here is an offer the submit
  // would reject.
  const { data: contexts } = useAvailableContextsInOrg(incident?.organizationId);
  const { activeContextId } = useActiveContext();

  // Start on the context the user is already working in. Only fills an empty
  // field, so a deliberate choice survives closing and reopening the sheet.
  // Set during render (guarded, so it settles in one pass) rather than in an
  // effect, which would paint the empty select first.
  const preferredRemediateContext = preferredContext(contexts, activeContextId);
  if (!remediateContextId && preferredRemediateContext) {
    setRemediateContextId(preferredRemediateContext.id);
  }
  const remediateMutation = useCreateCMFromIncident();
  const cmEnabled = useFeature('changeManagement');

  // Everything related to this incident, each row carrying why. The server decides
  // which incidents are the same occurrence — grouping by correlation here would put
  // this finding's entire history in the list.
  const { data: relatedData } = useRelatedIncidents(id);
  const allRelated = relatedData ?? [];
  // This panel speaks only about audience partitions, so it lists only those. A
  // future `caused_by` edge is a different statement and needs its own presentation
  // rather than inheriting a heading that would misdescribe it.
  const relatedIncidents = allRelated.filter((r) => r.relation === 'partition');
  // Resolved partitions are worth *reading* — "Division B closed theirs an hour ago"
  // is one of the more useful things this page says — but they are not worth acting
  // on: they notify nobody and there is nothing left to remediate. Everything that
  // counts or changes partitions counts the live ones.
  const livePartitions = relatedIncidents.filter((r) => r.resolvedAt === null);
  // Division names, so a sibling reads as "Globex" rather than as a uuid. An
  // organization-level partition has no division and says so.
  const { data: divisions } = useDivisions(incident?.organizationId);
  const divisionName = (divisionId: string | null) => {
    if (!divisionId) return 'Organization-level';
    return divisions?.find((d) => d.id === divisionId)?.name ?? 'Another division';
  };

  // Suppress stops the alert firing for the whole organization, so it is gated on
  // the same permission as editing the alert that fires it. Mute is personal and
  // always available.
  const canSuppress = usePermission('alert_definition:update');

  // What is silencing this alert right now, asked of the backend rather than
  // remembered from the last click — a suppression survives a reload, and one made
  // elsewhere (the alert's Overrides panel, a region-wide bulk silence) suppresses
  // this incident just as effectively.
  const { data: suppressions } = useIncidentSuppressions(id);
  const activeSuppression: IncidentSuppression | undefined = suppressions?.[0];
  const unsuppressMutation = useUnsuppressIncident();
  const fetchSuppressionImpact = useSuppressionImpactFetcher();
  const { confirm, confirmDialog } = useConfirm();

  const handleUnsuppress = async (suppression: IncidentSuppression) => {
    if (!id) return;
    // The blast radius is only counted here, on the way to the confirm — it costs a
    // scan of the alert's other open incidents, which nobody should pay for just by
    // opening this page.
    let others: number | null;
    try {
      others = (await fetchSuppressionImpact(id, suppression.id)).otherOpenIncidents;
    } catch {
      // A failed count must not block the removal; the confirm falls back to
      // describing the scope in words.
      others = null;
    }

    // With the count missing, fall back to what the *shape* of the override
    // guarantees. `scopedToThisIncident` is the server's own answer to "does this
    // cover anything else?" — a device-scoped override with this incident's exact
    // dedupe key provably cannot, so warning that it "may" would be false alarm on
    // the most common path (the one this page's own Suppress button creates).
    const alsoAffects =
      others === null
        ? suppression.scopedToThisIncident
          ? 'It covers only this finding, so nothing else is affected. (The exact count could not be checked just now.)'
          : `It covers ${suppressionScopeLabel(suppression)}, so removing it may let the alert fire again for other findings too.`
        : others === 0
          ? 'It covers only this finding, so nothing else is affected.'
          : `It also suppresses ${String(others)} other open ${others === 1 ? 'finding' : 'findings'} — those will be able to notify again.`;

    if (
      !(await confirm({
        title: 'Remove suppression?',
        message: `This lets the alert fire and notify again for everyone in your organization. ${alsoAffects}`,
        confirmText: 'Remove suppression',
        isDanger: true,
      }))
    ) {
      return;
    }

    try {
      await unsuppressMutation.mutateAsync({ incidentId: id, overrideId: suppression.id });
      setRerunNotice({ kind: 'success', text: 'Suppression removed. This alert can notify again.' });
    } catch {
      setRerunNotice({ kind: 'error', text: 'Failed to remove the suppression.' });
    }
  };

  const { data: mutes } = useNotificationMutes();
  const muteMutation = useMuteSource();
  const unmuteMutation = useUnmuteSource();
  const isMuted =
    mutes?.some((m) => m.sourceType === MUTE_SOURCE_INCIDENT && m.sourceId === id) ?? false;

  const handleToggleMute = async () => {
    if (!id) return;
    const args = { sourceType: MUTE_SOURCE_INCIDENT, sourceId: id };
    try {
      // No confirmation: muting only changes what *you* are told, and it is
      // undone by the same button.
      if (isMuted) {
        await unmuteMutation.mutateAsync(args);
      } else {
        await muteMutation.mutateAsync(args);
      }
    } catch {
      setSubmitError(isMuted ? 'Failed to unmute this incident.' : 'Failed to mute this incident.');
    }
  };

  const remediations: IncidentRemediationOption[] = incident?.remediations ?? [];
  // Require an explicit choice only when the audit offered more than one fix.
  const remediationChoiceNeeded = remediations.length > 1 && !selectedRemediationId;

  const openRemediate = () => {
    // Pre-select the sole option so the common case is one click.
    setSelectedRemediationId(remediations.length === 1 ? remediations[0].id : '');
    // Reset the width each time. A sheet that remembers "the whole finding" from a
    // previous change would widen the next one by default, which is the wrong
    // direction for a choice that reaches other divisions' hardware.
    setRemediateWholeFinding(false);
    setIsRemediateOpen(true);
  };

  const openSuppress = () => {
    setSuppressScope('incident');
    setIsSuppressOpen(true);
  };

  const handleRemediate = async () => {
    if (!id || !remediateContextId || remediationChoiceNeeded) return;
    try {
      const cm = await remediateMutation.mutateAsync({
        // The whole correlation, or just this partition. Only the siblings this user
        // may read are ever in the list, so there is no way to reach another
        // audience's devices through it.
        incidentId: id,
        incidentIds: remediateWholeFinding ? livePartitions.map((r) => r.id) : undefined,
        contextId: remediateContextId,
        remediationId: selectedRemediationId || undefined,
      });
      setIsRemediateOpen(false);
      void navigate(`/change-management/${cm.id}`);
    } catch {
      setSubmitError('Failed to create a remediation change.');
    }
  };

  const acknowledgeMutation = useAcknowledgeIncident();
  const resolveMutation = useResolveIncident();
  const archiveMutation = useArchiveIncident();
  const addCommentMutation = useAddComment();
  const updateMutation = useUpdateIncident();
  const executeMutation = useExecuteAlertDefinition();
  const suppressMutation = useSuppressIncident();

  const openEditModal = () => {
    if (!incident) return;
    setEditTitle(incident.title);
    setEditDescription(incident.description ?? '');
    setEditSeverity(incident.severity);
    setIsEditOpen(true);
  };

  const handleEdit = async (e?: SubmitEvent) => {
    e?.preventDefault();
    if (!id) return;
    try {
      await updateMutation.mutateAsync({
        incidentId: id,
        data: { title: editTitle, description: editDescription, severity: editSeverity },
      });
      setIsEditOpen(false);
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to update incident.');
    }
  };

  const handleAcknowledge = async () => {
    if (!id) return;
    try {
      await acknowledgeMutation.mutateAsync(id);
    } catch {
      setSubmitError('Failed to acknowledge incident.');
    }
  };
  const handleResolve = async () => {
    if (!id) return;
    try {
      await resolveMutation.mutateAsync(id);
    } catch {
      setSubmitError('Failed to resolve incident.');
    }
  };
  const handleArchive = async () => {
    if (!id) return;
    try {
      await archiveMutation.mutateAsync(id);
    } catch {
      setSubmitError('Failed to archive incident.');
    }
  };

  const handleAddComment = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!id || !commentText.trim()) return;
    try {
      await addCommentMutation.mutateAsync({ incidentId: id, data: { content: commentText } });
      setCommentText('');
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to add comment.');
    }
  };

  const handleRerun = async () => {
    if (!incident?.taskId) return;
    setRerunNotice(null);
    try {
      await executeMutation.mutateAsync(incident.taskId);
      setRerunNotice({ kind: 'success', text: 'Alert queued for immediate re-evaluation.' });
    } catch (err) {
      if (err instanceof ExecuteCooldownError) {
        setRerunNotice({ kind: 'warning', text: err.message });
      } else {
        setRerunNotice({ kind: 'error', text: 'Failed to re-run alert.' });
      }
    }
  };

  const handleSuppress = async () => {
    if (!id || !incident?.taskId || !incident.dedupeKey) return;
    // No extra confirm step: the sheet itself is the confirmation. It is titled
    // "Suppress for everyone", states the org-wide blast radius in its description,
    // and takes a duration and a reason before its submit button — more deliberate
    // than a yes/no dialog, which stacked on top would just be a second click.
    //
    // Posts to the incident's own suppressions route rather than creating an
    // override directly: the server derives the scope and dedupe pattern from the
    // incident (so "suppress this finding" can't widen into "suppress everything
    // this alert reports") and records the timeline entry with real attribution.
    try {
      await suppressMutation.mutateAsync({
        incidentId: id,
        data: {
          silenceHours: suppressHours > 0 ? suppressHours : undefined,
          silenceReason: suppressReason || undefined,
          scope: suppressScope,
        },
      });
      setIsSuppressOpen(false);
      setSuppressReason('');
      setRerunNotice({
        kind: 'success',
        text:
          suppressScope === 'finding'
            ? 'The whole finding is suppressed, including the partitions you cannot see. The open incidents stay open; future firings won’t notify.'
            : 'This alert is suppressed. The existing incident stays open; future firings won’t notify.',
      });
    } catch {
      setRerunNotice({ kind: 'error', text: 'Failed to suppress this alert.' });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-4 w-1/3 mb-6" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  if (isError || !incident) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading incident:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => { void navigate('/incidents'); }}>
          <ChevronLeft className="w-4 h-4" />
          Back to Incidents
        </Button>
      </PageContainer>
    );
  }

  // Primary action surfaces based on status. Only one primary at a
  // time — the rest live in the secondary chip / mobile overflow menu.
  const primaryAction =
    incident.status === 'created' ? (
      <Button
        onClick={() => { void handleAcknowledge(); }}
        disabled={acknowledgeMutation.isPending}
      >
        <Check className="w-4 h-4" />
        Acknowledge
      </Button>
    ) : incident.status === 'acknowledged' ? (
      <Button
        onClick={() => { void handleResolve(); }}
        disabled={resolveMutation.isPending}
      >
        <CheckCheck className="w-4 h-4" />
        Resolve
      </Button>
    ) : incident.status === 'resolved' && !incident.archivedAt ? (
      <Button
        variant="outline"
        onClick={() => { void handleArchive(); }}
        disabled={archiveMutation.isPending}
      >
        <Archive className="w-4 h-4" />
        Archive
      </Button>
    ) : null;

  return (
    <PageContainer>
      {/* Breadcrumb (desktop) / Back chevron (mobile) */}
      <nav className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-3" aria-label="Breadcrumb">
        <Link to="/incidents" className="hover:text-foreground">Incidents</Link>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className="text-foreground truncate">{incident.title}</span>
      </nav>
      <Link
        to="/incidents"
        className="md:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" />
        All incidents
      </Link>

      {/* Page header — custom (not PageHeader primitive) because we
          need badge row in the subtitle slot and multiple actions. */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <SeverityBadge severity={incident.severity} />
            <StatusBadge status={incident.status} />
            {incident.quiet ? <QuietBadge /> : null}
            {incident.archivedAt ? (
              <Badge variant="outline" className="text-muted-foreground">archived</Badge>
            ) : null}
          </div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight break-words">
            {incident.title}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={openEditModal}>
            <Pencil className="w-4 h-4" />
            <span className="hidden md:inline">Edit</span>
          </Button>
          {/* Hidden while a suppression is already in force — the banner below owns
              that state and offers removal. Offering "Suppress" again would stack a
              second override that removing the first one wouldn't clear. */}
          {incident.taskId && incident.dedupeKey && canSuppress && !activeSuppression ? (
            <Button
              variant="outline"
              size="sm"
              onClick={openSuppress}
              title={SUPPRESS_HELPER}
            >
              <Ban className="w-4 h-4" />
              <span className="hidden md:inline">Suppress for everyone</span>
            </Button>
          ) : null}
          {/* Nothing left to remediate once it's resolved — the change would be
              raised to fix a condition that has already cleared, and the tracking
              it offers ("did this change resolve the incident?") has no question
              left to answer. Archived is excluded for the same reason. */}
          {!incident.archivedAt && incident.status !== 'resolved' && cmEnabled ? (
            <Button
              variant="outline"
              size="sm"
              onClick={openRemediate}
              title="Create a change management to fix this incident; it'll track whether the change resolved it."
            >
              <GitPullRequest className="w-4 h-4" />
              <span className="hidden md:inline">Remediate</span>
            </Button>
          ) : null}
          {primaryAction}
        </div>
      </div>

      {/* Suppression state — persistent, derived from the alert's overrides rather
          than from the last click, and NOT dismissable: it describes how the system
          is configured right now, so hiding it would recreate the bug where a
          silenced alert looked normal. */}
      {activeSuppression ? (
        <Alert className="mb-4 border-sev-3 bg-sev-3-soft text-foreground">
          <Ban className="h-4 w-4" />
          <AlertDescription>
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <span className="font-medium">
                  {activeSuppression.overrideType === 'disable'
                    ? 'This alert is disabled for everyone.'
                    : 'This alert is suppressed for everyone.'}
                </span>{' '}
                <span className="text-muted-foreground">
                  Covers {suppressionScopeLabel(activeSuppression)},{' '}
                  {suppressionWindowLabel(activeSuppression)}. It will not notify anyone until
                  the suppression is removed; the incident stays open.
                </span>
                {activeSuppression.silenceReason ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Reason: “{activeSuppression.silenceReason}”
                  </div>
                ) : null}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  by user {activeSuppression.createdBy.slice(0, 8)} ·{' '}
                  {format(new Date(activeSuppression.createdAt), 'PPp')}
                  {suppressions && suppressions.length > 1
                    ? ` · ${String(suppressions.length - 1)} further ${suppressions.length === 2 ? 'suppression' : 'suppressions'} also cover this alert`
                    : ''}
                </div>
              </div>
              {canSuppress ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => { void handleUnsuppress(activeSuppression); }}
                  disabled={unsuppressMutation.isPending}
                >
                  <Bell className="w-3.5 h-3.5" />
                  {unsuppressMutation.isPending ? 'Removing…' : 'Remove suppression'}
                </Button>
              ) : null}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Notices — dismissable */}
      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {rerunNotice ? (
        <Alert
          variant={rerunNotice.kind === 'error' ? 'destructive' : 'default'}
          className={cn(
            'mb-4 relative',
            rerunNotice.kind === 'warning' && 'border-sev-3 bg-sev-3-soft text-foreground',
            rerunNotice.kind === 'success' && 'border-sev-4 bg-sev-4/10 text-foreground',
          )}
        >
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{rerunNotice.text}</AlertDescription>
          <button
            type="button"
            onClick={() => { setRerunNotice(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {/* 2/3 + 1/3 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          {/* One evaluation, one incident per audience. Rendered only when this
              incident actually has siblings the viewer may see — which for most
              incidents is never, so the common case gains no clutter at all. */}
          {relatedIncidents.length > 0 ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-1 flex items-center gap-2">
                <Layers className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                Part of a wider finding
              </h2>
              <p className="text-xs text-muted-foreground mb-3">
                This problem spans devices in more than one division. Each division gets its
                own incident naming only its own devices; you are seeing{' '}
                <strong className="text-foreground">{divisionName(incident.divisionId)}</strong>
                {"'"}s.
              </p>
              <ul className="space-y-1.5">
                {relatedIncidents.map((sibling) => (
                  <li key={sibling.id}>
                    <Link
                      to={`/incidents/${sibling.id}`}
                      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border-subtle px-3 py-2 text-sm hover:bg-accent"
                    >
                      <span className="font-medium truncate flex-1 min-w-0">
                        {divisionName(sibling.divisionId)}
                      </span>
                      {/* Without a time, a closed partition is indistinguishable from
                          stale data — "closed theirs an hour ago" is the useful
                          sentence, and only the timestamp carries it. */}
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {sibling.resolvedAt
                          ? `Resolved ${formatDistanceToNow(new Date(sibling.resolvedAt), { addSuffix: true })}`
                          : `Opened ${formatDistanceToNow(new Date(sibling.createdAt), { addSuffix: true })}`}
                      </span>
                      <SeverityBadge severity={sibling.severity} />
                      <StatusBadge status={sibling.status} />
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-muted-foreground mt-2">
                Only the partitions you have permission to read are listed. There may be
                others.
              </p>
            </section>
          ) : null}

          <IncidentRollupPanel incident={incident} />

          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Description</h2>
            {incident.description ? (
              <Markdown>{incident.description}</Markdown>
            ) : (
              <p className="text-sm text-muted-foreground">No description provided.</p>
            )}
          </section>

          {/* Directly under the description, because together they are the
              whole answer: the description states the rule that broke, the
              chart shows how far and for how long. Renders nothing for
              incidents with no metric behind them. */}
          <IncidentMetricChart incidentId={incident.id} live={incident.resolvedAt === null} />

          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Timeline</h2>
            {eventsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : events && events.length > 0 ? (
              <ol className="relative space-y-4 pl-6 before:absolute before:left-3 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border-subtle">
                {events.map((event) => (
                  <TimelineRow
                    key={event.id}
                    event={event}
                    firingTaskName={firingTask?.name ?? null}
                  />
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">No events yet.</p>
            )}
          </section>

          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Add comment</h2>
            <form onSubmit={(e) => { void handleAddComment(e); }} className="space-y-3">
              <Textarea
                value={commentText}
                onChange={(e) => { setCommentText(e.target.value); }}
                placeholder="Add a comment…"
                rows={3}
              />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={!commentText.trim() || addCommentMutation.isPending}
                >
                  <MessageSquare className="w-4 h-4" />
                  {addCommentMutation.isPending ? 'Posting…' : 'Post comment'}
                </Button>
              </div>
            </form>
          </section>
        </div>

        <aside className="space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Details</h2>
            <dl className="text-xs space-y-2">
              <DetailRow
                label="Created"
                value={format(new Date(incident.createdAt), 'PPpp')}
              />
              <DetailRow
                label="Updated"
                value={formatDistanceToNow(new Date(incident.updatedAt), { addSuffix: true })}
              />
              {incident.acknowledgedAt ? (
                <DetailRow
                  label="Acknowledged"
                  value={format(new Date(incident.acknowledgedAt), 'PPpp')}
                />
              ) : null}
              {incident.resolvedAt ? (
                <DetailRow
                  label="Resolved"
                  value={format(new Date(incident.resolvedAt), 'PPpp')}
                />
              ) : null}
              {incident.archivedAt ? (
                <DetailRow
                  label="Archived"
                  value={format(new Date(incident.archivedAt), 'PPpp')}
                />
              ) : null}
            </dl>
          </section>

          {psaTicketing ? <IncidentTickets incidentId={incident.id} /> : null}

          {incident.affectedDevices && incident.affectedDevices.length > 0 ? (
            <AffectedDevicesSection devices={incident.affectedDevices} />
          ) : null}

          {incident.taskId ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-3">Fired by alert</h2>
              <div className="text-sm mb-3">
                <Link
                  to={`/alerts/${incident.taskId}`}
                  className="text-primary hover:underline truncate inline-block max-w-full"
                >
                  {firingTask ? firingTask.name : `${incident.taskId.slice(0, 8)}…`}
                </Link>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void handleRerun(); }}
                disabled={executeMutation.isPending}
                title="Queue this alert for immediate re-evaluation (limited to once per minute)"
              >
                <RefreshCw
                  className={cn('w-3.5 h-3.5', executeMutation.isPending && 'animate-spin')}
                />
                Re-run alert
              </Button>
            </section>
          ) : null}

          {/* Mute lives here rather than beside "Suppress for everyone" in the
              header: physical separation carries more of the personal-vs-org
              distinction than the wording does. */}
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Notifications</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void handleToggleMute(); }}
              disabled={muteMutation.isPending || unmuteMutation.isPending}
              title={isMuted ? MUTED_HELPER : MUTE_HELPER}
            >
              {isMuted ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              {isMuted ? 'Muted — unmute' : 'Mute for me'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              {isMuted ? MUTED_HELPER : MUTE_HELPER}
            </p>

            {/* The record of who was notified sits beside the control that stops
                you being notified — the delivery log is what makes "why didn't I
                hear about this?" answerable, and Mute is the answer to the
                opposite question. */}
            <div className="mt-4 pt-4 border-t border-border">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Delivery
              </h3>
              <IncidentNotifications incidentId={id} />
            </div>
          </section>
        </aside>
      </div>

      {/* Edit modal */}
      <AppSheet open={isEditOpen} onOpenChange={setIsEditOpen} title="Edit incident">
        <AppSheet.Body>
          <form
            id="edit-incident-form"
            onSubmit={(e) => { void handleEdit(e); }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="edit-incident-title">Title</Label>
              <Input
                id="edit-incident-title"
                value={editTitle}
                onChange={(e) => { setEditTitle(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-incident-description">Description</Label>
              <Textarea
                id="edit-incident-description"
                value={editDescription}
                onChange={(e) => { setEditDescription(e.target.value); }}
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Markdown supported — bullet lists, <code>{'`code`'}</code>, **bold**, [links](https://example.com), tables, etc.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-incident-severity">Severity</Label>
              <Select
                value={editSeverity}
                onValueChange={(v) => { setEditSeverity(v as IncidentSeverity); }}
              >
                <SelectTrigger id="edit-incident-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsEditOpen(false); }}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-incident-form"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Suppress modal */}
      <AppSheet
        open={isSuppressOpen}
        onOpenChange={setIsSuppressOpen}
        title="Suppress for everyone"
        description={`${SUPPRESS_HELPER} The current incident stays open — resolve it manually if it's actually fixed. To quiet just your own notifications, use "Mute for me" instead.`}
      >
        <AppSheet.Body>
          <div className="text-xs text-muted-foreground mb-4 p-3 bg-bg-subtle rounded-md border border-border-subtle">
            Match:{' '}
            <code className="font-mono text-foreground">
              {suppressScope === 'finding' ? `${incident.correlationKey ?? ''}:*` : incident.dedupeKey}
            </code>{' '}
            on <strong>{firingTask?.name ?? 'this alert'}</strong>
            {suppressScope === 'incident' && incident.deviceId ? (
              <>
                {' '}— scoped to <strong>this device only</strong>
              </>
            ) : null}
          </div>

          <form
            id="suppress-incident-form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSuppress();
            }}
            className="space-y-4"
          >
            {/* Offered only when there is a wider finding to widen to. Defaulting to
                this partition keeps the narrow choice the easy one: silencing the
                whole finding quiets divisions whose devices this user may never
                have seen. */}
            {incident.correlationKey ? (
              <div className="space-y-1.5">
                <Label>How much to silence</Label>
                <RadioGroup
                  value={suppressScope}
                  onValueChange={(v) => { setSuppressScope(v as 'incident' | 'finding'); }}
                  className="space-y-2 pt-1"
                >
                  <label
                    className={cn(
                      'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                      suppressScope === 'incident' ? 'border-primary bg-brand-soft/40' : 'border-border',
                    )}
                  >
                    <RadioGroupItem value="incident" className="mt-0.5" />
                    <div className="text-sm">
                      <strong>Just this incident</strong>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Silences only the devices you administer. The other{' '}
                        {livePartitions.length} partition
                        {livePartitions.length === 1 ? '' : 's'} keep notifying their own
                        divisions.
                      </p>
                    </div>
                  </label>
                  <label
                    className={cn(
                      'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                      suppressScope === 'finding' ? 'border-primary bg-brand-soft/40' : 'border-border',
                    )}
                  >
                    <RadioGroupItem value="finding" className="mt-0.5" />
                    <div className="text-sm">
                      <strong>The whole finding</strong>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Silences every partition of this finding, present and future —
                        including any in divisions you cannot see.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="suppress-hours">Silence for</Label>
              <Select
                value={String(suppressHours)}
                onValueChange={(v) => { setSuppressHours(parseInt(v, 10)); }}
              >
                <SelectTrigger id="suppress-hours">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                  <SelectItem value="0">Indefinite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="suppress-reason">Reason (optional)</Label>
              <Input
                id="suppress-reason"
                value={suppressReason}
                onChange={(e) => { setSuppressReason(e.target.value); }}
                placeholder="e.g., known multihomed server"
              />
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsSuppressOpen(false); }}
            disabled={suppressMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="suppress-incident-form"
            disabled={suppressMutation.isPending}
            className="bg-sev-3 hover:bg-sev-3/90 text-foreground"
          >
            {suppressMutation.isPending ? 'Suppressing…' : 'Suppress for everyone'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Remediate — create a change to fix this incident. */}
      <AppSheet open={isRemediateOpen} onOpenChange={setIsRemediateOpen} title="Remediate with a change">
        <AppSheet.Body>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Creates a draft change management to fix <strong>{incident.title}</strong>. The
              incident is linked so the change tracks whether it resolved.
            </p>

            {/* One change across the whole correlation. Only the siblings this user
                may read are ever in the list, so widening cannot reach another
                audience's devices — and `materialize` re-checks `device:update` per
                device on top of that. */}
            {livePartitions.length > 0 ? (
              <label
                className={cn(
                  'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                  remediateWholeFinding ? 'border-primary bg-brand-soft/40' : 'border-border',
                )}
              >
                <Checkbox
                  checked={remediateWholeFinding}
                  onCheckedChange={(v) => { setRemediateWholeFinding(v === true); }}
                  className="mt-0.5"
                />
                <div className="text-sm">
                  <strong>
                    Fix the whole finding ({livePartitions.length + 1} incidents)
                  </strong>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Adds the {livePartitions.length} related incident
                    {livePartitions.length === 1 ? '' : 's'} to this change, each
                    contributing its own devices. Any whose devices you cannot reconfigure
                    are reported on the change rather than silently dropped.
                  </p>
                </div>
              </label>
            ) : null}

            {remediations.length > 0 ? (
              <div className="space-y-1.5">
                <Label>
                  Suggested fix {remediations.length > 1 ? '*' : ''}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {remediations.length > 1
                    ? 'Choose how to remediate. The steps land in the draft change for you to review before running.'
                    : 'This fix is pre-filled into the draft change for you to review before running.'}
                </p>
                <RadioGroup
                  value={selectedRemediationId}
                  onValueChange={setSelectedRemediationId}
                  className="space-y-2 pt-1"
                >
                  {remediations.map((opt) => (
                    <label
                      key={opt.id}
                      className={cn(
                        'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                        selectedRemediationId === opt.id
                          ? 'border-primary bg-brand-soft/40'
                          : 'border-border',
                      )}
                    >
                      <RadioGroupItem value={opt.id} className="mt-0.5" />
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <strong>{opt.title}</strong>
                          <Badge variant="secondary" className="text-[10px]">
                            {opt.stepCount} step{opt.stepCount === 1 ? '' : 's'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No automatic fix is available for this incident, so the change starts without
                steps for you to fill in.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="remediate-context">Context *</Label>
              <Select value={remediateContextId} onValueChange={setRemediateContextId}>
                <SelectTrigger id="remediate-context">
                  <SelectValue placeholder="Select a context" />
                </SelectTrigger>
                <SelectContent>
                  {(contexts ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsRemediateOpen(false); }}
            disabled={remediateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { void handleRemediate(); }}
            disabled={remediateMutation.isPending || !remediateContextId || remediationChoiceNeeded}
          >
            {remediateMutation.isPending ? 'Creating…' : 'Create change'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Removing a suppression has no sheet in front of it, so it confirms here. */}
      {confirmDialog}
    </PageContainer>
  );
}

// ─── Detail-row helper ───────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground flex-shrink-0">{label}</dt>
      <dd className="text-right truncate min-w-0">{value}</dd>
    </div>
  );
}

// ─── Affected-devices sidecar section ────────────────────────────────

/**
 * Sidecar block listing every device the incident concerns, the one
 * way the page shows them: a single-device finding's own device, and
 * every device a cross-device audit named (VLAN naming, MSTP region
 * mismatch, duplicate MAC, ...). Empty, and not rendered, only for an
 * incident that names no device at all.
 *
 * Role chips:
 *  - `majority` — the canonical/winning value (green)
 *  - `minority` — the dissenters that drove the incident (amber)
 *  - anything else — neutral chip with the raw role text
 *
 * When majority/minority are present we split into two visually
 * distinct groups so the operator immediately sees who's drifting.
 */
function AffectedDevicesSection({ devices }: { devices: IncidentAffectedDevice[] }) {
  const majority = devices.filter((d) => d.role === 'majority');
  const minority = devices.filter((d) => d.role === 'minority');
  const other = devices.filter((d) => d.role !== 'majority' && d.role !== 'minority');
  const isMajorityRule = majority.length > 0 && minority.length > 0;

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h2 className="text-sm font-semibold mb-3">
        Affected devices{' '}
        <span className="text-xs text-muted-foreground font-normal">
          ({devices.length})
        </span>
      </h2>
      {isMajorityRule ? (
        <div className="space-y-3">
          <DeviceGroup
            label={`Expected (${String(majority.length)})`}
            tone="majority"
            devices={majority}
          />
          <DeviceGroup
            label={`Outliers (${String(minority.length)})`}
            tone="minority"
            devices={minority}
          />
          {other.length > 0 ? (
            <DeviceGroup label="Other" tone="neutral" devices={other} />
          ) : null}
        </div>
      ) : (
        <ul className="space-y-2">
          {devices.map((d) => (
            <AffectedDeviceRow key={d.deviceId} device={d} />
          ))}
        </ul>
      )}
    </section>
  );
}

function DeviceGroup({
  label,
  tone,
  devices,
}: {
  label: string;
  tone: 'majority' | 'minority' | 'neutral';
  devices: IncidentAffectedDevice[];
}) {
  return (
    <div>
      <div
        className={cn(
          'text-[10px] uppercase tracking-wide font-semibold mb-1.5',
          tone === 'majority' && 'text-sev-4',
          tone === 'minority' && 'text-sev-3',
          tone === 'neutral' && 'text-muted-foreground',
        )}
      >
        {label}
      </div>
      <ul className="space-y-1.5 pl-0.5">
        {devices.map((d) => (
          <AffectedDeviceRow key={d.deviceId} device={d} />
        ))}
      </ul>
    </div>
  );
}

function AffectedDeviceRow({ device }: { device: IncidentAffectedDevice }) {
  return (
    <li className="text-xs">
      <Link
        to={`/devices/${device.deviceId}`}
        className="text-primary hover:underline font-medium break-words"
      >
        {device.deviceName}
      </Link>
      {device.note ? (
        <div className="text-muted-foreground mt-0.5 break-words">
          {device.note}
        </div>
      ) : null}
    </li>
  );
}

// ─── Timeline row ────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  eventType: string;
  createdAt: string;
  content?: string | null;
  createdBy: string | null;
  metadata: Record<string, unknown> | null;
}

function EventIcon({ eventType, className }: { eventType: string; className: string }) {
  switch (eventType) {
    case 'created':
      return <Flag className={className} aria-hidden="true" />;
    case 'field_changed':
      return <Pen className={className} aria-hidden="true" />;
    case 'comment':
      return <MessageSquare className={className} aria-hidden="true" />;
    case 'acknowledged':
      return <Check className={className} aria-hidden="true" />;
    case 'resolved':
      return <CheckCheck className={className} aria-hidden="true" />;
    case 'archived':
      return <Archive className={className} aria-hidden="true" />;
    case 'status_changed':
      return <RotateCcw className={className} aria-hidden="true" />;
    case 'suppressed':
      return <Ban className={className} aria-hidden="true" />;
    case 'unsuppressed':
      return <Bell className={className} aria-hidden="true" />;
    case 'remediation_blocked':
      return <ShieldAlert className={className} aria-hidden="true" />;
    case 'rolled_up':
      return <Layers className={className} aria-hidden="true" />;
    case 'released':
      return <RotateCcw className={className} aria-hidden="true" />;
    case 'ticket_linked':
    case 'ticket_closed':
    case 'ticket_reopened':
    case 'ticket_detached':
      return <Ticket className={className} aria-hidden="true" />;
    default:
      return <Flag className={className} aria-hidden="true" />;
  }
}

/**
 * Timeline heading. Most types read correctly as their own name with underscores
 * stripped; the suppression pair does not — "Suppressed"/"Unsuppressed" alone
 * invites the same personal-vs-org confusion the buttons were relabelled to avoid.
 */
function eventLabel(eventType: string): string {
  switch (eventType) {
    case 'suppressed':
      return 'Suppressed for everyone';
    case 'unsuppressed':
      return 'Suppression removed';
    case 'remediation_blocked':
      return 'Scripted fix not added';
    // Rollup: said in full, because "rolled up" alone is easy to read as "closed".
    case 'rolled_up':
      return 'Rolled up into a broader incident — still open';
    case 'released':
      return 'No longer rolled up';
    case 'ticket_linked':
      return 'Ticket linked';
    case 'ticket_closed':
      return 'Ticket closed in the PSA';
    case 'ticket_reopened':
      return 'Ticket reopened in the PSA';
    case 'ticket_detached':
      return 'Ticket no longer synced';
    default:
      return eventType.replace(/_/g, ' ');
  }
}

function renderActor(event: TimelineEvent, firingTaskName: string | null) {
  // A PSA integration acts as itself, naming the PSA person when it can.
  const viaIntegration = integrationActorLabel(event.metadata);
  if (viaIntegration) {
    return <span className="text-muted-foreground">{viaIntegration}</span>;
  }
  if (event.createdBy === null) {
    // Prefer the alert's human-readable name (already fetched by the
    // parent component) over the opaque task UUID. Falls back to the
    // first 8 chars of the UUID when the alert was deleted or we
    // don't have it loaded — better than "by System" alone.
    const rawTaskId =
      event.metadata && typeof event.metadata === 'object'
        ? (event.metadata as { task_id?: unknown }).task_id
        : undefined;
    const label =
      firingTaskName ??
      (typeof rawTaskId === 'string' ? rawTaskId.slice(0, 8) : null);
    return (
      <span className="text-muted-foreground">
        by System{label ? ` (${label})` : ''}
      </span>
    );
  }
  return (
    <span className="text-muted-foreground">by user {event.createdBy.slice(0, 8)}</span>
  );
}

function renderDiffValue(v: unknown): string {
  if (v === null || v === undefined) return '(none)';
  if (typeof v === 'string') {
    if (v.length === 0) return '(empty)';
    return v.length > 80 ? `${v.slice(0, 80)}…` : v;
  }
  return JSON.stringify(v);
}

function FieldChanges({ metadata }: { metadata: Record<string, unknown> | null }) {
  if (!metadata || typeof metadata !== 'object') return null;
  const changes = (metadata as { changes?: Record<string, unknown> }).changes;
  if (!changes || typeof changes !== 'object') return null;
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;
  return (
    <ul className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
      {entries.map(([field, diff]) => {
        const d = diff as { old?: unknown; new?: unknown };
        return (
          <li key={field}>
            <span className="font-medium text-foreground">{field}</span>:{' '}
            <span
              className="line-through opacity-70"
              title={typeof d.old === 'string' ? d.old : undefined}
            >
              {renderDiffValue(d.old)}
            </span>
            {' → '}
            <span
              className="text-foreground"
              title={typeof d.new === 'string' ? d.new : undefined}
            >
              {renderDiffValue(d.new)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Why a system event happened, and which incident it points at — the resolution reason
 * ("the device answered after this incident opened"), the rollup parent, the incident
 * this one was superseded by. The backend has always recorded these in the event's
 * metadata; without this the timeline showed "resolved by System" and dropped the why.
 */
function EventReason({ metadata }: { metadata: Record<string, unknown> | null }) {
  if (!metadata || typeof metadata !== 'object') return null;
  const str = (k: string): string | null => {
    const v = metadata[k];
    return typeof v === 'string' ? v : null;
  };
  const reason = str('reason');
  const linked: [string, string | null][] = [
    ['Filed under', str('parent_incident_id')],
    ['Was filed under', str('former_parent_incident_id')],
    ['Taken over by', str('subsumed_by')],
  ];
  const links = linked.filter((l): l is [string, string] => l[1] !== null);
  if (!reason && links.length === 0) return null;
  return (
    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
      {reason ? <p>{reason.charAt(0).toUpperCase() + reason.slice(1)}.</p> : null}
      {links.map(([label, id]) => (
        <p key={label}>
          {label}{' '}
          <Link to={`/incidents/${id}`} className="underline hover:text-foreground">
            this incident
          </Link>
        </p>
      ))}
    </div>
  );
}

function TimelineRow({
  event,
  firingTaskName,
}: {
  event: TimelineEvent;
  firingTaskName: string | null;
}) {
  return (
    <li className="relative">
      <span className="absolute -left-6 top-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-subtle border border-border text-muted-foreground">
        <EventIcon eventType={event.eventType} className="w-3 h-3" />
      </span>
      <div className="text-[11px] text-muted-foreground">
        {format(new Date(event.createdAt), 'PPpp')}
      </div>
      <div className="text-sm">
        <span className="font-medium capitalize">{eventLabel(event.eventType)}</span>{' '}
        {renderActor(event, firingTaskName)}
        {psaVisibilityLabel(event.metadata) ? (
          <Badge variant="outline" className="ml-2 font-normal">
            {psaVisibilityLabel(event.metadata)}
          </Badge>
        ) : null}
      </div>
      {event.eventType === 'field_changed' ? <FieldChanges metadata={event.metadata} /> : null}
      <EventReason metadata={event.metadata} />
      {event.content ? (
        <p
          className={cn(
            'text-sm mt-1.5',
            // This one is a warning about something that did not happen, not a note
            // about something that did — it has to survive a skim of the timeline.
            event.eventType === 'remediation_blocked'
              ? 'text-sev-2 bg-sev-2/10 border border-sev-2/20 rounded px-2 py-1.5'
              : 'text-foreground'
          )}
        >
          {event.content}
        </p>
      ) : null}
    </li>
  );
}

// ─── Severity + status badges (mirror Incidents.tsx) ─────────────────

const STATUS_STYLE: Record<IncidentStatus, string> = {
  created: 'bg-sev-2/15 text-sev-2',
  acknowledged: 'bg-sev-5/15 text-sev-5',
  resolved: 'bg-sev-4/15 text-sev-4',
  archived: 'bg-muted text-muted-foreground',
};

function StatusBadge({ status }: { status: IncidentStatus }) {
  return (
    <Badge className={cn('border-0 font-normal capitalize', STATUS_STYLE[status])}>
      {status}
    </Badge>
  );
}
